import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaCalendarAlt, FaPlus, FaPencilAlt, FaTrash } from "react-icons/fa";
import StaffLayout from "./StaffLayout";
import { workforceAvailabilityAPI } from "../../services/api";
import { getErrorMessage, handleApiError } from "../../utils/errorUtils";
import "./StaffAvailabilityPage.css";

/**
 * SCHED-SLICE1-H — Staff Availability Management (staff-self-service CRUD).
 *
 * Route: /staff/my-availability (role-gated to staff/admin/massage_therapist
 * in App.js via the standard <ProtectedRoute> shell).
 *
 * Consumes SCHED-SLICE1-D backend endpoints (PR #130, merged 2026-05-12):
 *   GET    /api/workforce/availability       — list own windows
 *   POST   /api/workforce/availability       — create (server-derived
 *                                               staffProfileId; the FE
 *                                               never passes one)
 *   PUT    /api/workforce/availability/:id   — update
 *   DELETE /api/workforce/availability/:id   — delete
 *
 * Visual posture: standard studio FE CRUD pattern (gold primary CTAs,
 * teal secondary, error-red destructive) — no separate Zuri spec per
 * scope-efficiency call 2026-05-12 (Roderick directive). Mirrors the
 * JobCodeList shape with availability-specific fields.
 *
 * Production-safety:
 *   - Pure-additive page; no existing surface mutated.
 *   - Single-revert posture (delete this file + its CSS + un-mount
 *     route + delete the sidebar link).
 *   - No env vars, no schema changes.
 *   - NOT a Customer-Facing Gate (R-020) — staff-internal feature, same
 *     as the rest of Slice 1.
 *   - Hard Rule § N/A — no customer surface.
 */

// Day-of-week roster — Sunday=0 matches the BE Availability model and the
// AC Scenario 9 sort order (Sun top, Sat bottom).
const DAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// HH:MM 24-hour pattern — mirrors workforceStaffShiftsController.HHMM_PATTERN.
// FE re-checks fast-fail before submit; the BE re-validates.
const HHMM_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const MAX_NOTES_LENGTH = 280;

const dayLabel = (value) => {
  const entry = DAY_OPTIONS.find((d) => d.value === value);
  return entry ? entry.label : `Day ${value}`;
};

/**
 * Format an HH:MM 24-hour string as "9:00 AM MST" / "5:30 PM MST" for the
 * list-row display. Matches the studio-wide MST convention; no moment
 * dependency since we are formatting a clock-time-of-day, not an instant.
 */
const formatTime12h = (hhmm) => {
  if (typeof hhmm !== "string" || !HHMM_PATTERN.test(hhmm)) return hhmm || "";
  const [h, m] = hhmm.split(":").map((part) => parseInt(part, 10));
  const period = h < 12 ? "AM" : "PM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, "0")} ${period} MST`;
};

/**
 * Default effectiveDate when staff opens the Add form — today (Phoenix-MST
 * neutral; we send an ISO at midnight UTC which the BE accepts as a valid
 * ISO 8601 datetime). The Availability model uses effectiveDate as the
 * "starts on" date for the recurring weekly window; for a staff member
 * adding "Tuesday 9-5", "today" is the right default.
 */
const todayIsoMidnightUtc = () => {
  const now = new Date();
  const ymd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
  return `${ymd}T00:00:00.000Z`;
};

const emptyForm = () => ({
  dayOfWeek: 1, // Monday default
  startTime: "09:00",
  endTime: "17:00",
  notes: "",
});

const StaffAvailabilityPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");

  // Modal state. `modalMode` is either "add" or "edit". `editingRecord` is
  // the record being edited (null on add). `formError` is an inline banner
  // inside the modal; distinct from `listError` (page-level).
  const [modalMode, setModalMode] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Delete-confirmation modal state.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toast state — single-slot rolling toast. Auto-dismiss after ~3s.
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // Focus management — capture the trigger so we can return focus on close.
  const triggerRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const deleteCancelRef = useRef(null);

  const showToast = useCallback((variant, message) => {
    setToast({ variant, message });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const response = await workforceAvailabilityAPI.listMine();
      const list = Array.isArray(response.data?.availability)
        ? response.data.availability
        : [];
      setRecords(list);
      setListError("");
    } catch (err) {
      handleApiError(
        err,
        "StaffAvailabilityPage.fetch",
        setListError,
        "Could not load your availability. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Sorted view — the BE already sorts by (dayOfWeek asc, startTime asc) per
  // SCHED-1D listMyAvailability. We re-sort defensively in case the response
  // shape drifts or a future polling refresh races a mutation.
  const sortedRecords = useMemo(() => {
    const copy = [...records];
    copy.sort((a, b) => {
      if (a.dayOfWeek !== b.dayOfWeek) return a.dayOfWeek - b.dayOfWeek;
      const at = a.startTime || "";
      const bt = b.startTime || "";
      return at.localeCompare(bt);
    });
    return copy;
  }, [records]);

  // Form-validation derived state. The Save button is enabled only when
  // every field is valid; mirror of the BE validator (Scenarios 4 + 7).
  const formValid = useMemo(() => {
    const { dayOfWeek, startTime, endTime, notes } = form;
    if (
      typeof dayOfWeek !== "number" ||
      !Number.isInteger(dayOfWeek) ||
      dayOfWeek < 0 ||
      dayOfWeek > 6
    ) {
      return false;
    }
    if (!HHMM_PATTERN.test(startTime)) return false;
    if (!HHMM_PATTERN.test(endTime)) return false;
    // String compare on HH:MM 24-hour is lexicographically equivalent to
    // numeric time-of-day compare (matches the BE validator idiom).
    if (endTime <= startTime) return false;
    if (typeof notes === "string" && notes.length > MAX_NOTES_LENGTH) {
      return false;
    }
    return true;
  }, [form]);

  // Specific inline error for the time-range violation — surfaced only when
  // both fields parse as valid HH:MM AND endTime <= startTime.
  const timeRangeError = useMemo(() => {
    if (!HHMM_PATTERN.test(form.startTime)) return "";
    if (!HHMM_PATTERN.test(form.endTime)) return "";
    if (form.endTime <= form.startTime) {
      return "End time must be after start time.";
    }
    return "";
  }, [form.startTime, form.endTime]);

  const openAdd = (event) => {
    if (event && event.currentTarget) {
      triggerRef.current = event.currentTarget;
    }
    setEditingRecord(null);
    setForm(emptyForm());
    setFormError("");
    setModalMode("add");
  };

  const openEdit = (record, event) => {
    if (event && event.currentTarget) {
      triggerRef.current = event.currentTarget;
    }
    setEditingRecord(record);
    setForm({
      dayOfWeek: record.dayOfWeek,
      startTime: record.startTime,
      endTime: record.endTime,
      notes: typeof record.notes === "string" ? record.notes : "",
    });
    setFormError("");
    setModalMode("edit");
  };

  const closeFormModal = useCallback(() => {
    setModalMode(null);
    setEditingRecord(null);
    setFormError("");
    // Return focus to the trigger that opened the modal.
    if (triggerRef.current && typeof triggerRef.current.focus === "function") {
      // Defer one tick so the modal unmount completes before focus moves.
      setTimeout(() => {
        if (
          triggerRef.current &&
          typeof triggerRef.current.focus === "function"
        ) {
          triggerRef.current.focus();
        }
      }, 0);
    }
  }, []);

  const handleFormFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildSubmitBody = () => {
    const trimmedNotes =
      typeof form.notes === "string" ? form.notes.trim() : "";
    const body = {
      dayOfWeek: form.dayOfWeek,
      startTime: form.startTime,
      endTime: form.endTime,
    };
    // notes is optional — only send when non-empty to keep the BE row
    // shape minimal.
    if (trimmedNotes.length > 0) {
      body.notes = trimmedNotes;
    } else if (modalMode === "edit") {
      // On edit, sending null clears any previous notes value.
      body.notes = null;
    }
    return body;
  };

  const handleSubmit = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    if (!formValid || submitting) return;
    setFormError("");
    setSubmitting(true);
    try {
      if (modalMode === "add") {
        const body = {
          ...buildSubmitBody(),
          // effectiveDate is required by the BE (POST validator). Default
          // to today — staff can refine later via edit if a future-dated
          // window is needed. (Optional `endDate` is omitted = open-ended.)
          effectiveDate: todayIsoMidnightUtc(),
        };
        await workforceAvailabilityAPI.create(body);
        showToast("success", "Availability window added.");
      } else if (modalMode === "edit" && editingRecord) {
        const body = buildSubmitBody();
        await workforceAvailabilityAPI.update(editingRecord._id, body);
        showToast("success", "Availability window updated.");
      }
      closeFormModal();
      await fetchAvailability();
    } catch (err) {
      // 400 / 5xx / network — render inline banner in the modal; keep the
      // modal open so the staff member does not lose their entered values.
      const message = getErrorMessage(
        err,
        "Could not save your availability. Please try again."
      );
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const requestDelete = (record, event) => {
    if (event && event.currentTarget) {
      triggerRef.current = event.currentTarget;
    }
    setPendingDelete(record);
  };

  const cancelDelete = () => {
    setPendingDelete(null);
    if (triggerRef.current && typeof triggerRef.current.focus === "function") {
      setTimeout(() => {
        if (
          triggerRef.current &&
          typeof triggerRef.current.focus === "function"
        ) {
          triggerRef.current.focus();
        }
      }, 0);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await workforceAvailabilityAPI.remove(pendingDelete._id);
      setPendingDelete(null);
      showToast("success", "Availability window deleted.");
      await fetchAvailability();
    } catch (err) {
      const message = getErrorMessage(
        err,
        "Could not delete the availability window. Please try again."
      );
      setListError(message);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // Focus the Cancel button on form-modal open (a11y: predictable initial
  // focus inside the dialog).
  useEffect(() => {
    if (modalMode && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [modalMode]);

  // Focus the Cancel button on delete-modal open (Scenario 6 — Cancel is
  // the default action and receives focus per a11y).
  useEffect(() => {
    if (pendingDelete && deleteCancelRef.current) {
      deleteCancelRef.current.focus();
    }
  }, [pendingDelete]);

  // Escape key closes the modal currently open.
  useEffect(() => {
    if (!modalMode && !pendingDelete) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (pendingDelete && !deleting) {
          cancelDelete();
        } else if (modalMode && !submitting) {
          closeFormModal();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalMode, pendingDelete, submitting, deleting, closeFormModal]);

  const renderList = () => {
    if (loading) {
      return (
        <p className="staff-avail-loading" aria-live="polite">
          Loading your availability...
        </p>
      );
    }
    if (sortedRecords.length === 0) {
      return (
        <div className="staff-avail-empty" role="status">
          <FaCalendarAlt
            className="staff-avail-empty-icon"
            aria-hidden="true"
          />
          <h2 className="staff-avail-empty-heading">
            You haven't set any availability windows yet.
          </h2>
          <p className="staff-avail-empty-body">
            Add your first one so your admin can see when you're available
            to work.
          </p>
          <button
            type="button"
            className="staff-avail-btn staff-avail-btn-primary staff-avail-empty-cta"
            onClick={openAdd}
          >
            <FaPlus aria-hidden="true" />
            <span>Add availability window</span>
          </button>
        </div>
      );
    }
    return (
      <ul className="staff-avail-list" aria-label="Your availability windows">
        {sortedRecords.map((record) => {
          const day = dayLabel(record.dayOfWeek);
          const startDisplay = formatTime12h(record.startTime);
          const endDisplay = formatTime12h(record.endTime);
          const editLabel = `Edit ${day} availability from ${startDisplay} to ${endDisplay}`;
          const deleteLabel = `Delete ${day} availability from ${startDisplay} to ${endDisplay}`;
          return (
            <li key={record._id} className="staff-avail-row">
              <div className="staff-avail-row-body">
                <span className="staff-avail-row-day">{day}</span>
                <span className="staff-avail-row-time">
                  {startDisplay} — {endDisplay}
                </span>
                {record.notes ? (
                  <span
                    className="staff-avail-row-notes"
                    title={record.notes}
                  >
                    {record.notes}
                  </span>
                ) : null}
              </div>
              <div className="staff-avail-row-actions">
                <button
                  type="button"
                  className="staff-avail-btn staff-avail-btn-secondary"
                  onClick={(e) => openEdit(record, e)}
                  aria-label={editLabel}
                >
                  <FaPencilAlt aria-hidden="true" />
                  <span>Edit</span>
                </button>
                <button
                  type="button"
                  className="staff-avail-btn staff-avail-btn-danger"
                  onClick={(e) => requestDelete(record, e)}
                  aria-label={deleteLabel}
                >
                  <FaTrash aria-hidden="true" />
                  <span>Delete</span>
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <StaffLayout>
      <div className="staff-avail-container">
        <header className="staff-avail-header">
          <div className="staff-avail-header-text">
            <h1>My Availability</h1>
            <p className="staff-avail-subtitle">
              Tell your admin which days and times you can work. Your admin
              uses these windows to detect conflicts before assigning
              shifts. All times are in Phoenix-MST.
            </p>
          </div>
          {!loading && sortedRecords.length > 0 ? (
            <button
              type="button"
              className="staff-avail-btn staff-avail-btn-primary staff-avail-header-cta"
              onClick={openAdd}
            >
              <FaPlus aria-hidden="true" />
              <span>Add availability window</span>
            </button>
          ) : null}
        </header>

        {listError ? (
          <div className="staff-avail-error" role="alert">
            {listError}
          </div>
        ) : null}

        <section className="staff-avail-list-section">{renderList()}</section>

        {modalMode ? (
          <div
            className="staff-avail-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-avail-modal-heading"
          >
            <form
              className="staff-avail-modal-card"
              onSubmit={handleSubmit}
              noValidate
            >
              <h2 id="staff-avail-modal-heading">
                {modalMode === "add"
                  ? "Add availability window"
                  : "Edit availability window"}
              </h2>
              <p className="staff-avail-modal-subtitle">
                All times are in Phoenix-MST.
              </p>

              {formError ? (
                <div className="staff-avail-modal-banner" role="alert">
                  {formError}
                </div>
              ) : null}

              <label
                className="staff-avail-field-label"
                htmlFor="staff-avail-day"
              >
                Day of week
              </label>
              <select
                id="staff-avail-day"
                className="staff-avail-input"
                value={form.dayOfWeek}
                onChange={(e) =>
                  handleFormFieldChange(
                    "dayOfWeek",
                    parseInt(e.target.value, 10)
                  )
                }
              >
                {DAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="staff-avail-time-row">
                <div className="staff-avail-time-field">
                  <label
                    className="staff-avail-field-label"
                    htmlFor="staff-avail-start"
                  >
                    Start time (MST)
                  </label>
                  <input
                    id="staff-avail-start"
                    type="time"
                    className="staff-avail-input"
                    value={form.startTime}
                    onChange={(e) =>
                      handleFormFieldChange("startTime", e.target.value)
                    }
                    required
                  />
                </div>
                <div className="staff-avail-time-field">
                  <label
                    className="staff-avail-field-label"
                    htmlFor="staff-avail-end"
                  >
                    End time (MST)
                  </label>
                  <input
                    id="staff-avail-end"
                    type="time"
                    className="staff-avail-input"
                    value={form.endTime}
                    onChange={(e) =>
                      handleFormFieldChange("endTime", e.target.value)
                    }
                    required
                    aria-describedby={
                      timeRangeError ? "staff-avail-end-error" : undefined
                    }
                  />
                </div>
              </div>
              {timeRangeError ? (
                <p
                  id="staff-avail-end-error"
                  className="staff-avail-inline-error"
                  role="alert"
                >
                  {timeRangeError}
                </p>
              ) : null}

              <label
                className="staff-avail-field-label"
                htmlFor="staff-avail-notes"
              >
                Notes (optional)
              </label>
              <textarea
                id="staff-avail-notes"
                className="staff-avail-textarea"
                value={form.notes || ""}
                onChange={(e) =>
                  handleFormFieldChange("notes", e.target.value)
                }
                maxLength={MAX_NOTES_LENGTH}
                rows={3}
                placeholder="Anything your admin should know about this window."
              />
              <p className="staff-avail-notes-counter" aria-live="polite">
                {(form.notes || "").length}/{MAX_NOTES_LENGTH}
              </p>

              <div className="staff-avail-modal-actions">
                <button
                  type="button"
                  className="staff-avail-btn staff-avail-btn-secondary"
                  onClick={closeFormModal}
                  disabled={submitting}
                  ref={cancelButtonRef}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="staff-avail-btn staff-avail-btn-primary"
                  disabled={!formValid || submitting}
                >
                  {submitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {pendingDelete ? (
          <div
            className="staff-avail-modal-backdrop"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="staff-avail-delete-heading"
            aria-describedby="staff-avail-delete-body"
          >
            <div className="staff-avail-modal-card staff-avail-modal-card-delete">
              <h2 id="staff-avail-delete-heading">
                Delete this availability window?
              </h2>
              <p id="staff-avail-delete-body">
                Your admin will no longer see this as a window when
                scheduling shifts.
              </p>
              <div className="staff-avail-modal-actions">
                <button
                  type="button"
                  className="staff-avail-btn staff-avail-btn-secondary"
                  onClick={cancelDelete}
                  disabled={deleting}
                  ref={deleteCancelRef}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="staff-avail-btn staff-avail-btn-danger-solid"
                  onClick={confirmDelete}
                  disabled={deleting}
                  aria-describedby="staff-avail-delete-body"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {toast ? (
          <div
            className={`staff-avail-toast staff-avail-toast-${toast.variant}`}
            role="status"
            aria-live="polite"
          >
            {toast.message}
          </div>
        ) : null}
      </div>
    </StaffLayout>
  );
};

export default StaffAvailabilityPage;
